import type { ChangeReason, SubjectPlan, VersionBump, VersionPlan } from "../../../api/content/versionDecoders";

/**
 * What the person merging confirms (ADR-0033 D3, built provisionally, awaiting acceptance):
 * DMW proposes a bump for the project content and for each item the change set modifies;
 * the person keeps or changes each, and writes one change line. Pure.
 */

/**
 * - `bump`: has a label and a proposal; the person chooses patch, minor or major.
 * - `new`: has no label yet, so it starts at 1.0.0.
 * - `removed`: gets no new label.
 * - `unchanged`: keeps its label.
 */
export type SubjectStatus = "bump" | "new" | "removed" | "unchanged";

export interface PlanRow {
  /** `null`: the project content as a whole. */
  readonly itemId: string | null;
  readonly status: SubjectStatus;
  readonly current: string | null;
  readonly proposed: string | null;
  /** DMW's proposal; set exactly when `status` is `bump`. */
  readonly proposal: VersionBump | null;
  readonly reasons: readonly ChangeReason[];
  readonly mayChangeMethod: boolean;
  /** What each choice would give, as the server computes it. */
  readonly versionsByBump: Readonly<Partial<Record<VersionBump, string>>>;
}

/** The person's changes to DMW's proposals; a subject not named keeps the proposal. */
export interface BumpChoices {
  readonly whole: VersionBump | null;
  readonly items: Readonly<Record<string, VersionBump>>;
}

export const NO_CHANGED_BUMPS: BumpChoices = { whole: null, items: {} };

/** What the merge sends: the bump for the project content and for each modified item. */
export interface ConfirmedBumps {
  readonly versionBump: VersionBump | null;
  readonly itemBumps: Readonly<Record<string, VersionBump>>;
}

function subjectStatus(subject: SubjectPlan): SubjectStatus {
  if (subject.removed) return "removed";
  if (subject.current === null) return "new";
  return subject.bump === null ? "unchanged" : "bump";
}

function planRow(subject: SubjectPlan): PlanRow {
  const status = subjectStatus(subject);
  return {
    itemId: subject.itemId,
    status,
    current: subject.current,
    proposed: status === "bump" ? subject.proposed : null,
    proposal: status === "bump" ? subject.bump : null,
    reasons: subject.reasons,
    mayChangeMethod: subject.mayChangeMethod,
    versionsByBump: status === "bump" ? subject.versionsByBump : {},
  };
}

/** One row per subject: the project content first, then the items. */
export function planRows(plan: VersionPlan): readonly PlanRow[] {
  return [planRow(plan.whole), ...plan.items.map(planRow)];
}

/** The bump a row has now: the person's choice, else DMW's proposal. */
export function chosenBump(row: PlanRow, choices: BumpChoices): VersionBump | null {
  if (row.status !== "bump") return null;
  const choice = row.itemId === null ? choices.whole : choices.items[row.itemId];
  return choice ?? row.proposal;
}

export function withChoice(choices: BumpChoices, row: PlanRow, bump: VersionBump): BumpChoices {
  return row.itemId === null ? { ...choices, whole: bump } : { ...choices, items: { ...choices.items, [row.itemId]: bump } };
}

/**
 * Every bump the person saw and kept or changed, so each is recorded as confirmed. New,
 * removed and unchanged subjects send nothing: the server refuses a bump for them.
 */
export function confirmedBumps(plan: VersionPlan, choices: BumpChoices): ConfirmedBumps {
  let versionBump: VersionBump | null = null;
  const itemBumps: Record<string, VersionBump> = {};
  for (const row of planRows(plan)) {
    const bump = chosenBump(row, choices);
    if (bump === null) continue;
    if (row.itemId === null) versionBump = bump;
    else itemBumps[row.itemId] = bump;
  }
  return { versionBump, itemBumps };
}
